import { useState } from 'react';
import { IconChevronLeft, IconCircleCheck, IconClockCheck, IconCurrentLocation, IconDots, IconFingerprint, IconInfoCircle, IconKey, IconLock, IconMapPin, IconTimeDuration0, IconUserPlus } from '@tabler/icons-react';
import { PageHeader } from '@hana/shell';
import { Button, Chip, Drawer, Input, Segmented, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './checkin.i18n';
import { dowOf, fdw } from '../home/view';
import { useMee } from '../useMee';
import s from './checkin.module.css';
import { FIELD_ADDR, HIST, OFFICE, OFFICE_DIST, PASSKEYS, WEEKDAY, isLate, shiftLabel, type Group } from './site';

// Chấm công điện thoại (board Mint-Checkin-Phone): 3 khung — Hôm nay · Quét khuôn mặt · Xác thực (khuôn mặt / passkey).
// Giả lập: không gọi camera / GPS thật, không ghi DB; giờ chấm giữ trong bộ nhớ, toast có Hoàn tác.
// Điều khiển xem thử như prop của board: `?group=field` (nhóm hiện trường) · `?state=…` (qua useMee).
const NOW_IN = '07:56';
const NOW_OUT = '17:34';
const SHOTS = 5;

type View = 'home' | 'scan' | 'verify';
type Step = 'ready' | 'scan' | 'ok' | 'fail';

function Rec({ cal, today, tin, tout, field, enrolled }: { cal: ReturnType<typeof useMee>['cal']; today: string; tin: string | null; tout: string | null; field: boolean; enrolled: boolean }) {
  const t = useT();
  const late = tin ? isLate(cal, today, tin) : false;
  return (
    <section aria-label={t('ci.today')} className={s.card}>
      <b className={s.h}>{t('ci.today')} · {WEEKDAY[dowOf(today)]} {fdw(today).split(' ').slice(1).join(' ')}</b>
      <div className={s.shift} title={t('ci.shiftTitle')}><IconTimeDuration0 size={13} stroke={1.8} aria-hidden="true" />{shiftLabel(cal, today)}</div>
      <div className={s.pair}>
        <div className={s.cell}><div className={s.lb}>{t('ci.tin')}</div><div className={s.big} data-on={!!tin}>{tin ?? '—'}</div><div className={s.sm}>{tin ? (late ? t('ci.late') : t('ci.onTime')) : t('ci.notYet')}</div></div>
        <div className={s.cell}><div className={s.lb}>{t('ci.tout')}</div><div className={s.big} data-on={!!tout}>{tout ?? '—'}</div><div className={s.sm}>{tout ? t('ci.worked') : tin ? t('ci.leaveHint') : '—'}</div></div>
      </div>
      <div className={s.chips}>
        <span className={s.pill}>{field ? t('ci.fieldPill') : t('ci.officePill', { r: OFFICE.radius })}</span>
        <span className={s.pill} data-tone={enrolled ? 'ok' : 'warn'}>{enrolled ? t('ci.enrolled') : t('ci.notEnrolled')}</span>
      </div>
    </section>
  );
}

export function CheckinScreen() {
  const t = useT();
  const m = useMee();
  const toast = useToast();
  const params = new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
  const field = (params.get('group') as Group | null) === 'field';
  const [view, setView] = useState<View>('home');
  const [tin, setTin] = useState<string | null>(null);
  const [tout, setTout] = useState<string | null>(null);
  const [kind, setKind] = useState<'in' | 'out'>('in');
  const [step, setStep] = useState<Step>('ready');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [gps, setGps] = useState(m.state !== 'error');
  const [site, setSite] = useState(false);
  const [siteName, setSiteName] = useState(OFFICE.name);
  const [siteR, setSiteR] = useState(String(OFFICE.radius));
  const [tips, setTips] = useState(false);
  const [tab, setTab] = useState<'face' | 'pk'>('face');
  const [shots, setShots] = useState(0);
  const [consent, setConsent] = useState(false);
  const [enrolledNow, setEnrolledNow] = useState(false);

  const enrolled = m.state !== 'empty' || enrolledNow;
  const addr = !gps ? t('ci.noLoc') : field ? FIELD_ADDR : OFFICE.addr;
  const geoK = !gps ? t('ci.geoOff') : field ? t('ci.geoField') : t('ci.geoOffice', { d: OFFICE_DIST, r: OFFICE.radius });
  const geoTone = !gps ? 'err' : field ? 'warn' : 'ok';

  const regps = () => { setGps(true); toast({ msg: t('ci.gotLoc') }); };
  const record = (k: 'in' | 'out', via: string) => {
    if (k === 'in') setTin(NOW_IN); else setTout(NOW_OUT);
    toast({ msg: `${k === 'in' ? t('ci.recIn', { h: NOW_IN }) : t('ci.recOut', { h: NOW_OUT })}${via}`, undo: () => (k === 'in' ? setTin(null) : setTout(null)) });
  };
  const start = (k: 'in' | 'out') => { setKind(k); setStep('ready'); setTried(false); setView('scan'); };
  const scan = () => {
    if (!gps) return toast({ msg: t('ci.needLoc') });
    if (!enrolled) return;
    if (field && !reason.trim()) return setTried(true);
    setStep('scan');
    window.setTimeout(() => { setStep('ok'); }, 1600);
  };
  const done = () => { record(kind, ''); setStep('ready'); setView('home'); };
  const passkey = () => {
    if (!gps) return toast({ msg: t('ci.needLoc') });
    if (!tin) record('in', t('ci.viaPk')); else if (!tout) record('out', t('ci.viaPk')); else toast({ msg: t('ci.allDoneToast') });
  };
  const enroll = () => {
    if (!consent) return toast({ msg: t('ci.needConsent') });
    if (shots >= SHOTS) return;
    const n = shots + 1;
    setShots(n);
    if (n >= SHOTS) { setEnrolledNow(true); toast({ msg: t('ci.enrolledToast') }); }
  };

  const back = (label: string, to: View) => <Button variant="secondary" aria-label={label} title={label} icon={<IconChevronLeft size={20} stroke={2} />} onClick={() => { setStep('ready'); setView(to); }} />;

  if (view === 'scan') {
    const pct = step === 'scan' ? 0.72 : step === 'ok' || step === 'fail' ? 1 : 0;
    const guide = !gps ? t('ci.waitGps') : !enrolled ? t('ci.needEnroll') : { ready: t('ci.gReady'), scan: t('ci.gScan'), ok: t('ci.gOk'), fail: t('ci.gFail') }[step];
    const tone = step === 'fail' || !gps ? 'err' : step === 'ok' ? 'ok' : '';
    const label = { ready: t('ci.bReady'), scan: t('ci.bScan'), ok: t('ci.bOk'), fail: t('ci.bFail') }[step];
    return (
      <div className={s.page}>
        <PageHeader lead={back(t('ci.cancelScan'), 'home')} title={kind === 'in' ? t('ci.clockIn') : t('ci.clockOut')} findHint={false}
          actions={<Button variant="secondary" aria-label={t('ci.tipsAria')} icon={<IconInfoCircle size={19} stroke={1.9} />} onClick={() => setTips(true)} />} />
        <StateView state={m.state === 'noperm' || m.state === 'loading' ? m.state : 'ready'} title={t('ci.offTitle')} desc={t('ci.offDesc')} skeleton={<Skeleton rows={4} />}>
          <div className={s.cam} role="img" aria-label={t('ci.camAria')}>
            <svg aria-hidden="true" width="260" height="320" viewBox="0 0 260 320">
              <ellipse cx="130" cy="160" rx="104" ry="136" className={s.ringBase} />
              <ellipse cx="130" cy="160" rx="104" ry="136" className={s.ring} data-tone={tone} pathLength="100" strokeDasharray={`${pct * 100} 100`} transform="rotate(-90 130 160)" />
              <circle cx="130" cy="128" r="44" className={s.sil} />
              <path d="M56 290c8-58 44-86 74-86s66 28 74 86" className={s.sil} />
            </svg>
            <span className={s.guide} data-tone={tone}>{guide}</span>
          </div>
          <div className={s.chips}>{[t('ci.tip1'), t('ci.tip2'), t('ci.tip3'), t('ci.tip4')].map((x) => <span key={x} className={s.tip}>{x}</span>)}</div>
          <div className={s.loc}><IconMapPin size={20} stroke={1.9} data-tone={geoTone} aria-hidden="true" /><div><div className={s.locA}>{addr}</div><div className={s.sm} data-tone={geoTone}>{geoK}</div></div></div>
          {field && (
            <label className={s.field}><span>{t('ci.reasonLb')} <b className={s.req}>*</b></span>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('ci.reasonPh')} aria-invalid={tried && !reason.trim()} /></label>
          )}
          <div className={s.foot}>
            <Button variant="primary" className={s.cta} disabled={!enrolled || step === 'ok'} onClick={scan}>{label}</Button>
            {step === 'fail' && <Button variant="secondary" className={s.alt} icon={<IconKey size={16} stroke={1.8} />} onClick={() => { setTab('pk'); setView('verify'); }}>{t('ci.usePk')}</Button>}
          </div>
        </StateView>
        <Drawer open={tips} onOpenChange={setTips} title={t('ci.tipsTitle')} footer={<Button variant="secondary" onClick={() => setTips(false)}>{t('ci.gotIt')}</Button>}>
          <ul className={s.tipList}><li>{t('ci.l1')}</li><li>{t('ci.l2')}</li><li>{t('ci.l3')}</li><li>{t('ci.l4')}</li><li>{t('ci.l5')}</li></ul>
        </Drawer>
        <Drawer open={step === 'ok'} onOpenChange={(o) => { if (!o) done(); }} title={kind === 'in' ? t('ci.doneIn', { h: NOW_IN }) : t('ci.doneOut', { h: NOW_OUT })} description={field ? t('ci.descField') : t('ci.descOffice')}
          footer={<Button variant="primary" icon={<IconCircleCheck size={16} stroke={1.8} />} onClick={done}>{t('ci.finish')}</Button>}>
          <dl className={s.res}>
            {[[t('ci.rDate'), fdw(m.today).split(' ')[1] ?? ''], [t('ci.rLoc'), addr], [t('ci.rDist'), field ? t('ci.rNA') : t('ci.rDistV', { d: OFFICE_DIST })], [t('ci.rMatch'), '0,36 · Valid'], [t('ci.rSrc'), 'gps-face'], ...(field ? [[t('ci.rReason'), reason || '—']] : [])].map(([k, v]) => <div key={k} className={s.resRow}><dt>{k}</dt><dd>{v}</dd></div>)}
          </dl>
        </Drawer>
      </div>
    );
  }

  if (view === 'verify') {
    const done5 = shots >= SHOTS;
    const showEnroll = !enrolled || shots > 0;
    return (
      <div className={s.page}>
        <PageHeader lead={back(t('ci.backCi'), 'home')} title={t('ci.verifyTitle')} findHint={false} />
        <Segmented label={t('ci.method')} value={tab} onChange={setTab} items={[{ key: 'face', label: t('ci.face') }, { key: 'pk', label: 'Passkey' }]} />
        <StateView state={m.state === 'noperm' || m.state === 'loading' ? m.state : 'ready'} title={t('ci.offAcct')} skeleton={<Skeleton rows={4} />}>
          {tab === 'face' ? (
            <>
              {enrolled && shots === 0 && (
                <div className={s.okBox}><b>{t('ci.enrolledOn', { d: '02/Oct/2026' })}</b><div className={s.sm} title={t('ci.relockTitle')}><IconLock size={14} stroke={1.8} aria-hidden="true" /> {t('ci.locked')}</div></div>
              )}
              {showEnroll && (
                <>
                  <div className={s.cam} data-enroll role="img" aria-label={t('ci.enrollAria')}>
                    <svg aria-hidden="true" width="190" height="240" viewBox="0 0 190 240"><ellipse cx="95" cy="120" rx="78" ry="102" className={s.ringBase} data-on={done5 ? 'ok' : shots ? 'pri' : ''} /><circle cx="95" cy="96" r="32" className={s.sil} /><path d="M40 216c6-42 32-62 55-62s49 20 55 62" className={s.sil} /></svg>
                  </div>
                  <div className={s.shots}>{Array.from({ length: SHOTS }, (_, i) => <span key={i} data-on={i < shots ? (done5 ? 'ok' : 'pri') : ''} />)}<b>{t('ci.samples', { n: shots })}</b></div>
                  <div className={s.center}>{done5 ? t('ci.enDone') : shots ? t('ci.enTurn') : t('ci.enHint')}</div>
                  <label className={s.consent}><input type="checkbox" checked={consent} onChange={() => setConsent(!consent)} /><span>{t('ci.consent1')} <b>{t('ci.consentB')}</b> {t('ci.consent2')}</span></label>
                  <Button variant="primary" className={s.cta} disabled={done5} onClick={enroll}>{done5 ? t('ci.enrolledBtn') : shots > 0 ? t('ci.nextShot', { n: shots }) : t('ci.startEnroll')}</Button>
                </>
              )}
            </>
          ) : (
            <>
              <div className={s.card}><div className={s.keyIc}><IconFingerprint size={26} stroke={1.8} aria-hidden="true" /></div><b>{t('ci.pkTitle')}</b><span className={s.lb}>{t('ci.pkDesc')}</span></div>
              <div className={s.devs}>{PASSKEYS.map((d) => <div key={d.k} className={s.dev}><div><b>{d.k}</b><div className={s.lb}>{d.v}</div></div>{d.me && <Chip tone="info">{t('ci.thisDevice')}</Chip>}</div>)}</div>
              <Button variant="primary" className={s.cta} icon={<IconClockCheck size={16} stroke={1.8} />} onClick={passkey}>{tin && !tout ? t('ci.pkOut') : t('ci.pkIn')}</Button>
              <Button variant="secondary" className={s.alt} icon={<IconUserPlus size={16} stroke={1.8} />} onClick={() => toast({ msg: t('ci.pkRegToast') })}>{t('ci.pkReg')}</Button>
            </>
          )}
        </StateView>
      </div>
    );
  }

  const all = !!tin && !!tout;
  return (
    <div className={s.page}>
      <PageHeader lead={back(t('ci.backMee'), 'home')} title={t('ci.title')} sub={`${m.me?.id ?? 'DAVID'} · ${t('ci.dept', { d: m.me?.dept ?? 'Kinh doanh' })}`} findHint={false}
        actions={<Button variant="secondary" aria-label={t('ci.more')} icon={<IconDots size={19} stroke={3} />} onClick={() => toast({ msg: t('ci.feedback') })} />} />
      <StateView state={m.state === 'error' ? 'ready' : m.state === 'empty' ? 'ready' : m.state} title={t('ci.offAcct2')}
        desc={t('ci.offAcctDesc')} skeleton={<div className={s.skel}><Skeleton rows={4} /><Skeleton rows={4} /></div>}>
        {!gps && (
          <div role="alert" className={s.alert}><span aria-hidden="true">!</span><div><b>{t('ci.locFail')}</b><div className={s.sm}>{t('ci.locFailDesc')}</div><Button variant="secondary" onClick={regps}>{t('ci.retry')}</Button></div></div>
        )}
        <Rec cal={m.cal} today={m.today} tin={tin} tout={tout} field={field} enrolled={enrolled} />
        <section aria-label={t('ci.locAria')} className={`${s.card} ${s.map}`}>
          <div className={s.mapArt} aria-hidden="true">
            <svg width="370" height="140" viewBox="0 0 370 140"><path d="M0 40h370 M0 96h370 M70 0v140 M190 0v140 M300 0v140 M0 130 370 10" className={s.road} />{!field && <circle cx="190" cy="70" r="52" className={s.geo} />}<circle cx="190" cy="70" r="5" className={s.pin} /><circle cx={field ? 300 : 214} cy="62" r="8" className={s.me} /></svg>
            <span>{t('ci.map')}</span>
          </div>
          <div className={s.locBody}>
            <div className={s.locA}>{addr}</div><div className={s.sm} data-tone={geoTone}>{geoK}</div>
            <div className={s.row}>
              <Button variant="secondary" icon={<IconCurrentLocation size={16} stroke={1.8} />} onClick={regps}>{t('ci.regps')}</Button>
              <Button variant="ghost" icon={<IconMapPin size={16} stroke={1.8} />} onClick={() => setSite(true)}>{t('ci.setSite')}</Button>
            </div>
          </div>
        </section>
        {!enrolled && <div className={s.warnBox}><b>{t('ci.notEnrolled')}</b><div className={s.sm}>{t('ci.warnDesc')}</div></div>}
        {!enrolled && <Button variant="primary" className={s.cta} icon={<IconUserPlus size={16} stroke={1.8} />} onClick={() => { setTab('face'); setView('verify'); }}>{t('ci.enrollBtn')}</Button>}
        {enrolled && !tin && <Button variant="primary" className={s.cta} disabled={!gps} icon={<IconClockCheck size={16} stroke={1.8} />} onClick={() => start('in')}>{t('ci.clockIn')}</Button>}
        {enrolled && !!tin && !tout && <Button variant="primary" className={s.cta} disabled={!gps} icon={<IconClockCheck size={16} stroke={1.8} />} onClick={() => start('out')}>{t('ci.clockOut')}</Button>}
        {all && <div className={s.allDone}>{t('ci.allDone')}</div>}
        {enrolled && !all && <Button variant="secondary" className={s.alt} icon={<IconKey size={16} stroke={1.8} />} onClick={() => { setTab('pk'); setView('verify'); }}>{t('ci.usePk')}</Button>}
        <section aria-label={t('ci.week')} className={s.card}>
          <div className={s.hrow}><b className={s.h}>{t('ci.week')}</b><span className={s.sm}>{t('ci.workDays', { n: 3 })}</span></div>
          {(m.state === 'empty' ? [] : HIST).map((h) => (
            <div key={h.dd} className={s.hist}>
              <div className={s.hd}><b>{h.d}</b><div className={s.sm}>{h.dd}</div></div>
              <div className={s.hb}><div>{h.io}</div><div className={s.sm} data-tone={h.flag ? 'warn' : ''}>{h.note}</div></div>
              {h.flag && <span className={s.flag}>{t('ci.flag')}</span>}
            </div>
          ))}
        </section>
      </StateView>
      <Drawer open={site} onOpenChange={setSite} title={t('ci.setSite')} description={t('ci.siteDesc')}
        footer={<><Button variant="secondary" onClick={() => setSite(false)}>{t('ci.cancel')}</Button><Button variant="primary" onClick={() => { setSite(false); toast({ msg: t('ci.siteSaved', { n: siteName, r: siteR }) }); }}>{t('ci.save')}</Button></>}>
        <label className={s.field}><span>{t('ci.siteName')}</span><Input value={siteName} onChange={(e) => setSiteName(e.target.value)} /></label>
        <label className={s.field}><span title={t('ci.radiusTitle')}>{t('ci.radius')}</span><Input value={siteR} onChange={(e) => setSiteR(e.target.value)} inputMode="numeric" /></label>
      </Drawer>
    </div>
  );
}
